/**
 * Export Dialog Component for MDM
 *
 * Purpose: Dialog for exporting master data values to CSV/Excel formats
 * Architecture: React dialog with export format selection and progress tracking
 * Dependencies: React, Material-UI, MDM service
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Radio,
  Switch,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  CloudDownload as ExportIcon,
  Close as CloseIcon,
  Description as FileIcon,
  TableChart as ExcelIcon,
  TextSnippet as CsvIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { LoadingButton } from '../common/LoadingButton';
import { styled } from '@mui/material/styles';
import { mdmService } from '../../services/mdmService';
import { ExportRequest } from '../../types/mdm';
import { SecondaryButton } from '../common/ActionButtons';

// Styled components
const ExportPreview = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
}));

const FormatOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.selected': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '10',
  },
}));

// Props interface
export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  fielddef_id: string;
  tenant_id: string;
  onExportComplete?: () => void;
}

// Export format options
interface FormatOptionType {
  value: 'xlsx' | 'csv';
  label: string;
  description: string;
  icon: React.ReactNode;
  mimeType: string;
}

const FORMAT_OPTIONS: FormatOptionType[] = [
  {
    value: 'xlsx',
    label: 'Excel (XLSX)',
    description: 'Rich formatting with multiple sheets support',
    icon: <ExcelIcon color="success" />,
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    value: 'csv',
    label: 'CSV',
    description: 'Simple comma-separated values format',
    icon: <CsvIcon color="primary" />,
    mimeType: 'text/csv',
  },
];

/**
 * Export Dialog Component
 *
 * Purpose: Handle export configuration and execution
 * Features: Format selection, global data inclusion, progress tracking
 */
const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  fielddef_id,
  tenant_id,
  onExportComplete,
}) => {
  // State management
  const [selectedFormat, setSelectedFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [includeGlobal, setIncludeGlobal] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Preview data (mock - in a real app, this might come from an API)
  const [previewData, setPreviewData] = useState<{
    totalValues: number;
    globalValues: number;
    localValues: number;
    activeValues: number;
    estimatedFileSize: string;
  } | null>(null);

  /**
   * Reset dialog state when opened/closed
   */
  useEffect(() => {
    if (open) {
      setSelectedFormat('xlsx');
      setIncludeGlobal(true);
      setExporting(false);
      setError(null);
      setSuccess(false);
      loadPreviewData();
    }
  }, [open]);

  /**
   * Load preview data for export
   */
  const loadPreviewData = async () => {
    try {
      // In a real implementation, this would call an API endpoint
      // For now, we'll simulate the data
      const filters = {
        fielddef_id,
        tenant_id,
        include_inactive: false,
        include_deleted: false,
      };

      const values = await mdmService.getValues(filters);

      const totalValues = values.length;
      const globalValues = values.filter((v) => v.scope_info.scope === 'GLOBAL').length;
      const localValues = values.filter((v) => v.scope_info.scope === 'LOCAL').length;
      const activeValues = values.filter((v) => v.is_active && !v.is_deleted).length;

      // Estimate file size (rough calculation)
      const avgRowSize = 100; // bytes per row
      const estimatedBytes = totalValues * avgRowSize;
      const estimatedFileSize = formatFileSize(estimatedBytes);

      setPreviewData({
        totalValues,
        globalValues,
        localValues,
        activeValues,
        estimatedFileSize,
      });
    } catch (err: any) {
      console.warn('Failed to load preview data:', err);
      // Don't show error to user for preview data failure
    }
  };

  /**
   * Handle export execution
   */
  const handleExport = async () => {
    setExporting(true);
    setError(null);
    setSuccess(false);

    try {
      const exportRequest: ExportRequest = {
        fielddef_id,
        tenant_id,
        format: selectedFormat,
        include_global: includeGlobal,
      };

      const blob = await mdmService.exportValues(exportRequest);

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `mdm_values_${fielddef_id}_${tenant_id}_${timestamp}.${selectedFormat}`;

      // Download file
      mdmService.downloadFile(blob, filename);

      setSuccess(true);
      onExportComplete?.();

      // Auto-close after successful export
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    if (!exporting) {
      onClose();
    }
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Get selected format details
   */
  const selectedFormatDetails = FORMAT_OPTIONS.find((f) => f.value === selectedFormat);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={exporting}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ExportIcon />
            <Typography variant="h6">Export Master Data Values</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={exporting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<SuccessIcon />}>
            Export completed successfully! The file has been downloaded to your computer.
          </Alert>
        )}

        {/* Export Configuration */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Configuration
          </Typography>

          {/* Format Selection */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Select Export Format
          </Typography>

          <Box display="flex" gap={2} mb={3}>
            {FORMAT_OPTIONS.map((format) => (
              <FormatOption
                key={format.value}
                className={selectedFormat === format.value ? 'selected' : ''}
                onClick={() => setSelectedFormat(format.value)}
                sx={{ flex: 1 }}
              >
                {format.icon}
                <Box>
                  <Typography variant="subtitle2">{format.label}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    {format.description}
                  </Typography>
                </Box>
                <Radio
                  checked={selectedFormat === format.value}
                  onChange={() => setSelectedFormat(format.value)}
                  size="small"
                />
              </FormatOption>
            ))}
          </Box>

          {/* Export Options */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Export Options
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={includeGlobal}
                onChange={(e) => setIncludeGlobal(e.target.checked)}
                disabled={exporting}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <GlobalIcon fontSize="small" />
                Include Global Values
              </Box>
            }
          />

          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mt: 0.5 }}>
            Include values from the GLOBAL tenant in addition to local tenant values
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Export Preview */}
        {previewData && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Export Preview
            </Typography>

            <ExportPreview>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <FileIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Values"
                    secondary={`${previewData.totalValues} values will be exported`}
                  />
                  <Chip label={previewData.totalValues} size="small" />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <GlobalIcon />
                  </ListItemIcon>
                  <ListItemText primary="Global Values" secondary="Values from GLOBAL tenant" />
                  <Chip
                    label={previewData.globalValues}
                    size="small"
                    color={includeGlobal ? 'primary' : 'default'}
                    variant={includeGlobal ? 'filled' : 'outlined'}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <TenantIcon />
                  </ListItemIcon>
                  <ListItemText primary="Local Values" secondary="Values from current tenant" />
                  <Chip label={previewData.localValues} size="small" color="secondary" />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <SuccessIcon />
                  </ListItemIcon>
                  <ListItemText primary="Active Values" secondary="Currently active values" />
                  <Chip label={previewData.activeValues} size="small" color="success" />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  Estimated file size: {previewData.estimatedFileSize}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Format: {selectedFormatDetails?.label}
                </Typography>
              </Box>
            </ExportPreview>
          </Box>
        )}

        {/* Additional Information */}
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            <strong>Export Details:</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Field Definition: {fielddef_id}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Tenant: {tenant_id}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Includes: Active values, metadata, scope information
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Excludes: Deleted values (unless specifically requested)
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={exporting}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          onClick={handleExport}
          loading={exporting}
          startIcon={<ExportIcon />}
          variant="contained"
          disabled={!previewData}
        >
          Export ({previewData?.totalValues || 0} values)
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
